import type { ErrorEvent, EventHint } from "@sentry/react";

export type SentryClientNoiseDecision = "drop" | "downgrade" | "keep";

const EXTENSION_SOURCE_PATTERNS = [
    /chrome-extension:\/\//i,
    /moz-extension:\/\//i,
    /safari-extension:\/\//i,
    /safari-web-extension:\/\//i,
    /ms-browser-extension:\/\//i,
    /extension:\/\//i,
    /webkit-masked-url/i,
];

const INJECTED_RESOURCE_PATTERNS = [
    /grammarly/i,
    /lastpass/i,
    /dashlane/i,
    /1password/i,
    /bitwarden/i,
    /metamask/i,
];

const CSP_PATTERNS = [
    /content security policy/i,
    /violates the following content security policy/i,
    /refused to (load|connect to|execute|frame|apply)/i,
];

const STORAGE_TRANSIENT_PATTERNS = [
    /quotaexceedederror/i,
    /the quota has been exceeded/i,
    /database connection is closing/i,
    /connection to the database is closing/i,
    /connection to indexed database server lost/i,
    /database deleted by request of the user/i,
    /unable to open database file on disk/i,
    /internal error opening backing store/i,
    /a mutation operation was attempted on a database that did not allow mutations/i,
    /failed to execute 'setitem' on 'storage'/i,
    /failed to execute '(open|transaction)' on '(idbdatabase|indexeddb|idbobjectstore)'/i,
];

const STORAGE_CONTEXT_PATTERNS = [
    /indexeddb/i,
    /\bidb/i,
    /localstorage/i,
    /sessionstorage/i,
    /\bstorage\b/i,
    /\bdatabase\b/i,
];

const STORAGE_QUOTA_PATTERNS = [/quotaexceedederror/i];

const FIREBASE_CONTEXT_PATTERNS = [
    /firebase/i,
    /firestore/i,
    /@firebase\//i,
    /firebaseerror/i,
];

const FIREBASE_TRANSIENT_PATTERNS = [
    /installations\/app-offline/i,
    /auth\/network-request-failed/i,
    /network-request-failed/i,
    /failed to get document because the client is offline/i,
    /client is offline/i,
    /application offline/i,
    /could not reach cloud firestore backend/i,
    /backend didn't respond within \d+ seconds/i,
    /deadline[-_ ]exceeded/i,
    /\bunavailable\b/i,
];

const DOM_ABORT_ERROR_PATTERNS = [
    /\baborterror\b/i,
    /the operation was aborted/i,
];

const DOM_ABORT_CONTEXT_PATTERNS = [
    /domexception\.code[:= ]?20/i,
    /global_handlers\.onunhandledrejection/i,
    /\bonunhandledrejection\b/i,
];

function containsPattern(value: string, patterns: RegExp[]) {
    return patterns.some((pattern) => pattern.test(value));
}

function getExceptionStrings(event: ErrorEvent) {
    return (
        event.exception?.values?.flatMap((exception) => {
            const frames =
                exception.stacktrace?.frames?.flatMap((frame) => [
                    frame.filename,
                    frame.module,
                    frame.function,
                ]) ?? [];

            return [exception.type, exception.value, ...frames];
        }) ?? []
    );
}

function getHintStrings(hint?: EventHint) {
    const originalException = hint?.originalException;
    const syntheticException = hint?.syntheticException;
    const values: Array<string | undefined> = [];

    if (typeof originalException === "string") {
        values.push(originalException);
    }

    if (originalException instanceof Error) {
        values.push(originalException.name, originalException.message);
    }

    if (syntheticException instanceof Error) {
        values.push(syntheticException.name, syntheticException.message);
    }

    return values;
}

function getTagStrings(event: ErrorEvent) {
    return Object.entries(event.tags ?? {}).map(
        ([key, value]) => `${key}:${String(value)}`
    );
}

function getEventText(event: ErrorEvent, hint?: EventHint) {
    return [
        event.message,
        event.transaction,
        ...getTagStrings(event),
        ...getExceptionStrings(event),
        ...getHintStrings(hint),
    ]
        .filter((value): value is string => Boolean(value))
        .join(" ");
}

function getFrameFilenames(event: ErrorEvent) {
    return (
        event.exception?.values?.flatMap(
            (exception) =>
                exception.stacktrace?.frames
                    ?.map((frame) => frame.filename)
                    .filter((value): value is string => Boolean(value)) ?? []
        ) ?? []
    );
}

function isExtensionOnlyStack(event: ErrorEvent) {
    const frameFilenames = getFrameFilenames(event);

    return (
        frameFilenames.length > 0 &&
        frameFilenames.every((filename) =>
            containsPattern(filename, EXTENSION_SOURCE_PATTERNS)
        )
    );
}

function isCspOrExtensionNoise(event: ErrorEvent, text: string) {
    const hasCspViolation = containsPattern(text, CSP_PATTERNS);
    const hasExtensionSource = containsPattern(text, EXTENSION_SOURCE_PATTERNS);
    const hasInjectedResource = containsPattern(
        text,
        INJECTED_RESOURCE_PATTERNS
    );

    return (
        (hasCspViolation && (hasExtensionSource || hasInjectedResource)) ||
        isExtensionOnlyStack(event)
    );
}

function isStorageNoise(text: string) {
    return (
        containsPattern(text, STORAGE_TRANSIENT_PATTERNS) &&
        (containsPattern(text, STORAGE_CONTEXT_PATTERNS) ||
            containsPattern(text, STORAGE_QUOTA_PATTERNS))
    );
}

function isFirebaseTransientNoise(text: string) {
    return (
        containsPattern(text, FIREBASE_CONTEXT_PATTERNS) &&
        containsPattern(text, FIREBASE_TRANSIENT_PATTERNS)
    );
}

function isDomAbortNoise(text: string) {
    return (
        containsPattern(text, DOM_ABORT_ERROR_PATTERNS) &&
        containsPattern(text, DOM_ABORT_CONTEXT_PATTERNS)
    );
}

export function classifySentryClientEvent(
    event: ErrorEvent,
    hint?: EventHint
): SentryClientNoiseDecision {
    const text = getEventText(event, hint);

    if (
        isCspOrExtensionNoise(event, text) ||
        isStorageNoise(text) ||
        isDomAbortNoise(text)
    ) {
        return "drop";
    }

    if (isFirebaseTransientNoise(text)) {
        return "downgrade";
    }

    return "keep";
}

export function filterSentryClientNoise(
    event: ErrorEvent,
    hint?: EventHint
): ErrorEvent | null {
    const decision = classifySentryClientEvent(event, hint);

    if (decision === "drop") {
        return null;
    }

    if (decision === "downgrade") {
        return {
            ...event,
            level: "warning",
            tags: {
                ...event.tags,
                client_noise_filter: "firebase-transient",
            },
        };
    }

    return event;
}
